// Sales Coach Réel — page de débrief (lecture détaillée d'une session)
import { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  ArrowLeft, Loader2, AlertCircle, CheckCircle2, XCircle, TrendingUp,
  Target, MessageSquare, Lightbulb, AlertTriangle, Sparkles,
  Mic, Bookmark, Lock, ChevronDown, ChevronUp, Clock, Type, Activity, Play, Volume2,
  StickyNote, Plus, Trash2, X, Share2, Link as LinkIcon, Check, Scissors,
} from "lucide-react";
import { salesCoachApi, type RealMeetingBridgeCardData } from "../lib/api";
import { AUDIO_RETENTION_DAYS, enregistrementPurge } from "../lib/sales-coach/audio-retention";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PrepareTrainingFromCoachDialog, type CoachTrainingContext } from "../components/PrepareTrainingFromCoachDialog";
import { RealMeetingBridgeCard } from "../components/RealMeetingBridgeCard";
import { ContextSpecificSection } from "../components/ContextSpecificSection";
import { pickScoringLabel } from "../lib/scoring-i18n";
import { companyMessageRegister } from "../lib/manager-nudge";
import type { RealCallSessionFull, RealCallScoreTier, RealCallContextType, DimensionScore } from "../types";

interface Props {
  sessionId: string;
  onBack: () => void;
  // Couture Pilier 1↔2 : lance le simulateur correspondant à la faiblesse identifiée.
  onStartTrainingForKey?: (simulatorKey: string) => void;
  // Étape 7 (pont bidirectionnel) : « préparer mon prochain RDV » → Coach Prepa.
  onPrepareMeeting?: () => void;
  /** Registre de l'entreprise (`client_accounts.message_register`, lu par `/api/auth/me`). */
  messageRegister?: "tu" | "vous";
}

// Les `labelKey` pointent vers les entrées i18n correspondantes — le label affiché
// est résolu à l'usage via t() pour respecter la langue de l'utilisateur.
const TIER_CONFIG: Record<RealCallScoreTier, { labelKey: string; bg: string; text: string; border: string }> = {
  excellent: { labelKey: "salesCoach.detail.qualityExcellent", bg: "#7CBF94", text: "#1E7A3C", border: "#1E7A3C" },
  good: { labelKey: "salesCoach.detail.qualityGood", bg: "#A6D3F9", text: "#384872", border: "#384872" },
  needs_improvement: { labelKey: "salesCoach.detail.qualityNeedsImprovement", bg: "#F6C847", text: "#926B00", border: "#926B00" },
  insufficient: { labelKey: "salesCoach.detail.qualityInsufficient", bg: "#F49390", text: "#C0423F", border: "#C0423F" },
};

const CONTEXT_LABEL_KEYS: Record<RealCallContextType, string> = {
  cold_call: "salesCoach.detail.contextColdCall",
  discovery: "salesCoach.detail.contextDiscovery",
  pitch: "salesCoach.detail.contextPitch",
  negotiation: "salesCoach.detail.contextNegotiation",
  customer_success: "salesCoach.detail.contextCustomerSuccess",
  qualification: "salesCoach.detail.contextQualification",
  generic: "salesCoach.detail.contextGeneric",
};

const IMPACT_CONFIG = {
  high: { labelKey: "salesCoach.detail.impactHigh", color: "#C0423F", bg: "#FFF5F5" },
  medium: { labelKey: "salesCoach.detail.impactMedium", color: "#926B00", bg: "#FFFBEB" },
  low: { labelKey: "salesCoach.detail.impactLow", color: "#384872", bg: "#F0F7FF" },
};

const HANDLING_CONFIG = {
  well_handled: { labelKey: "salesCoach.detail.handlingWellHandled", color: "#1E7A3C", bg: "#F0FDF4" },
  poorly_handled: { labelKey: "salesCoach.detail.handlingPoorlyHandled", color: "#C0423F", bg: "#FFF5F5" },
  evaded: { labelKey: "salesCoach.detail.handlingEvaded", color: "#926B00", bg: "#FFFBEB" },
};

const SIMULATOR_LABELS: Record<string, string> = {
  PROSPECTOR: "Prospector (cold call)",
  DISCOVERY: "Discovery (entretien découverte)",
  PITCH: "Pitch (présentation solution)",
  NEGOTIATOR: "Negotiator (négociation)",
  SATISFACTOR: "Satisfactor (Customer Success)",
  MEDDIC: "MEDDIC (qualification)",
};

/**
 * Texte d'exemple de la note du manager au commercial (décision Q14 de Valérie, 30/09/2026) : au
 * registre de l'ENTREPRISE, comme les relances (décision 6.2) — « tu » par défaut, « vous » sinon.
 */
export function coachNotePlaceholderKey(register: "tu" | "vous" | null | undefined): string {
  return register === "vous" ? "salesCoach.detail.notePlaceholderVous" : "salesCoach.detail.notePlaceholderTu";
}

export function SalesCoachDetailPage({ sessionId, onBack, onStartTrainingForKey, onPrepareMeeting, messageRegister }: Props) {
  const { t, i18n } = useTranslation();
  const [trainingDialogCtx, setTrainingDialogCtx] = useState<CoachTrainingContext | null>(null);
  const [session, setSession] = useState<RealCallSessionFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Étape 7 — carte violette « pont RDV réel → entraînement » (déclencheur administrable).
  const [bridge, setBridge] = useState<RealMeetingBridgeCardData | null>(null);

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    async function loadOnce() {
      try {
        const s = await salesCoachApi.getSession(sessionId);
        if (cancelled) return;
        setSession(s);
        setLoading(false);
        // Polling tant que l'analyse n'est pas terminée
        if (s.analysisStatus === "in_progress" || s.analysisStatus === "pending") {
          pollTimer = setTimeout(loadOnce, 1500);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t("salesCoach.detail.errorUnknown"));
        setLoading(false);
      }
    }

    loadOnce();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [sessionId, t]);

  // Étape 7 — récupère la carte « pont » une fois l'analyse terminée (silencieux :
  // absence de carte = pont désactivé ou aucun critère assez faible).
  useEffect(() => {
    if (session?.analysisStatus !== "completed") return;
    let cancelled = false;
    salesCoachApi
      .getBridge(sessionId)
      .then((c) => { if (!cancelled) setBridge(c); })
      .catch(() => { /* pas de pont : on n'affiche rien */ });
    return () => { cancelled = true; };
  }, [sessionId, session?.analysisStatus]);

  if (loading) {
    return (
      <div className="text-center py-20">
        <Loader2 className="h-10 w-10 animate-spin mx-auto mb-4" style={{ color: "#384872" }} />
        <p style={{ color: "#4B5573" }}>{t("salesCoach.detail.loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto">
        <button onClick={onBack} className="flex items-center gap-2 text-sm mb-4 hover:underline" style={{ color: "#384872" }}>
          <ArrowLeft className="h-4 w-4" /> {t("salesCoach.detail.back")}
        </button>
        <Card className="p-6 border-2" style={{ borderColor: "#F49390", background: "#FFF5F5" }}>
          <AlertCircle className="h-6 w-6 mb-2" style={{ color: "#C0423F" }} />
          <div className="font-semibold mb-1" style={{ color: "#C0423F" }}>{t("salesCoach.detail.error")}</div>
          <div className="text-sm" style={{ color: "#4B5573" }}>{error}</div>
        </Card>
      </div>
    );
  }

  if (!session) return null;

  // Statut d'analyse (polling automatique toutes les 1.5s en arrière-plan).
  // On affiche le WaitingScreen UNIQUEMENT tant qu'aucune partie du débrief n'est dispo.
  // Dès que la partie "quick" arrive (scoreGlobal présent), on affiche le débrief
  // partiel avec un loader sur les sections "advanced".
  const hasQuick = session.debriefJson && typeof session.debriefJson.scoreGlobal === "number";
  if ((session.analysisStatus === "in_progress" || session.analysisStatus === "pending") && !hasQuick) {
    return <WaitingScreen createdAt={session.createdAt} onBack={onBack} />;
  }

  if (session.analysisStatus === "failed") {
    return (
      <div className="max-w-3xl mx-auto">
        <button onClick={onBack} className="flex items-center gap-2 text-sm mb-4 hover:underline" style={{ color: "#384872" }}>
          <ArrowLeft className="h-4 w-4" /> {t("salesCoach.detail.back")}
        </button>
        <Card className="p-6 border-2" style={{ borderColor: "#F49390", background: "#FFF5F5" }}>
          <AlertCircle className="h-6 w-6 mb-2" style={{ color: "#C0423F" }} />
          <div className="font-semibold mb-1" style={{ color: "#C0423F" }}>{t("salesCoach.detail.analysisFailedTitle")}</div>
          <div className="text-sm" style={{ color: "#4B5573" }}>{t("salesCoach.detail.analysisFailedHint")}</div>
        </Card>
      </div>
    );
  }

  const debrief = session.debriefJson;
  if (!debrief) {
    return (
      <div className="max-w-3xl mx-auto">
        <button onClick={onBack} className="flex items-center gap-2 text-sm mb-4 hover:underline" style={{ color: "#384872" }}>
          <ArrowLeft className="h-4 w-4" /> {t("salesCoach.detail.back")}
        </button>
        <Card className="p-6">
          <p style={{ color: "#4B5573" }}>{t("salesCoach.detail.debriefUnavailable")}</p>
        </Card>
      </div>
    );
  }

  const tier = TIER_CONFIG[debrief.scoreTier];
  // Sections "advanced" (générées par l'appel Claude 2) : peuvent être absentes
  // si l'analyse advanced est encore en cours (split débrief progressif).
  const hasAdvanced = Array.isArray(debrief.dimensionScores) && debrief.dimensionScores.length > 0;
  const advancedLoading = !hasAdvanced && session.analysisStatus === "in_progress";

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-24 md:pb-0">
      {/* Retour */}
      <button onClick={onBack} className="flex items-center gap-2 text-sm hover:underline" style={{ color: "#384872" }}>
        <ArrowLeft className="h-4 w-4" /> {t("salesCoach.detail.backToSessions")}
      </button>

      {/* Header prospect personnalisé */}
      <ProspectHeader session={session} />

      {/* KPI cards en haut */}
      <KpiCards session={session} debrief={debrief} tier={tier} />

      {/* Tabs : Analyse / Transcription */}
      <Tabs defaultValue="analyse">
        <TabsList>
          <TabsTrigger value="analyse">{t("salesCoach.detail.tabAnalyse")}</TabsTrigger>
          <TabsTrigger value="transcript">{t("salesCoach.detail.tabTranscript")}</TabsTrigger>
        </TabsList>

        <TabsContent value="analyse" className="space-y-6 mt-6">
          {/* Recommandation d'entraînement — placée en TÊTE pour rendre l'action
              principale visible immédiatement (CTA fort dès l'arrivée sur la page). */}
          {debrief.recommendedSimulator && (
            <div className="rounded-xl border-2 bg-white p-4 md:p-5" style={{ borderColor: "#988EF2" }}>
              <div className="flex items-center gap-3 md:gap-4 mb-3">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "#988EF2" }}>
                  <Mic className="h-6 w-6 md:h-7 md:w-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-0.5">
                    {/* `priority` est un CODE renvoyé par l'analyse (`this_week` | `soon` |
                        `optional`), pas un texte destiné à l'écran. Il était injecté brut dans
                        la phrase après un simple remplacement du tiret bas : l'interface
                        française affichait « PRIORITÉ THIS WEEK ». On le résout par une table
                        de libellés, avec repli sur le code si l'analyse en renvoyait un
                        nouveau — mieux vaut un code affiché qu'une ligne vide. */}
                    {t("salesCoach.detail.priorityLabel", {
                      level: t(`salesCoach.detail.priorityLevels.${debrief.recommendedSimulator.priority}`, {
                        defaultValue: debrief.recommendedSimulator.priority.replace(/_/g, " "),
                      }),
                    })}
                  </div>
                  <h3 className="text-base md:text-lg font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                    {t("salesCoach.detail.trainOnSituation")}
                  </h3>
                  <p className="text-sm text-gray-600 mt-0.5">{t("salesCoach.detail.simulatorPrefix", { key: debrief.recommendedSimulator.simulatorKey, reason: debrief.recommendedSimulator.reason })}</p>
                </div>
              </div>
              {onStartTrainingForKey && (
                <button
                  onClick={() => setTrainingDialogCtx({
                    simulatorKey: debrief.recommendedSimulator.simulatorKey,
                    simulatorLabel: SIMULATOR_LABELS[debrief.recommendedSimulator.simulatorKey] ?? debrief.recommendedSimulator.simulatorKey,
                    axeTitle: t("salesCoach.detail.iaRecommendation"),
                    contextNotes: (session.contextNotes ?? undefined) || debrief.recommendedSimulator.reason,
                    scoreGlobal: debrief.scoreGlobal,
                    prospectName: session.contextProspectName,
                    prospectCompany: session.contextProspectCompany,
                    realCallSessionId: session.id,
                  })}
                  className="w-full md:w-auto inline-flex items-center justify-center gap-2 text-base font-semibold px-5 h-12 rounded-lg shadow-md hover:shadow-lg transition-shadow text-white"
                  style={{ backgroundColor: "#5A4BBE" }}
                >
                  <Mic className="h-5 w-5" />
                  {t("salesCoach.detail.launchMySimulation")}
                </button>
              )}
            </div>
          )}

          {/* Summary compact — fond pastel léger, padding réduit */}
          <div className="rounded-xl p-3 md:p-4" style={{ backgroundColor: `${tier.bg}25` }}>
            <div className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: tier.border }} />
              <p className="text-sm leading-snug" style={{ color: "#384872" }}>{debrief.summary}</p>
            </div>
          </div>

      {/* Bannière "analyse avancée en cours" si seulement quick prêt */}
      {advancedLoading && (
        <div className="rounded-xl p-4 flex items-center gap-3" style={{ backgroundColor: "#F8F7FF", border: "1px solid #988EF2" }}>
          <Loader2 className="h-5 w-5 animate-spin flex-shrink-0" style={{ color: "#5A4BBE" }} />
          <div className="text-sm" style={{ color: "#384872" }}>
            <strong>{t("salesCoach.detail.advancedLoadingTitle")}</strong> {t("salesCoach.detail.advancedLoadingHint")}
          </div>
        </div>
      )}

      {/* Scores par dimension — accordéon (lourd à scanner, replié par défaut sur mobile) */}
      {hasAdvanced && (
        <CollapsibleSection
          title={t("salesCoach.detail.scoresPerDimension")}
          icon={<Target className="h-5 w-5" />}
          iconColor="#384872"
          badge={`${debrief.dimensionScores.length}`}
          summary={buildDimensionsSummary(debrief.dimensionScores, t, i18n.language)}
        >
          <div className="space-y-6">
            {groupDimensionsByTheme(debrief.dimensionScores).map((group) => (
              <div key={group.themeKey}>
                <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 text-gray-500">
                  {group.emoji && <span className="mr-1.5">{group.emoji}</span>}
                  {group.isDbTheme
                    ? (group.labelKey ? t(group.labelKey) : (group.label ?? group.themeKey))
                    : t(group.themeKey)}
                  <span className="ml-2 font-normal text-gray-400">· {t("salesCoach.detail.dimensionsCountAbbrev", { count: group.items.length })}</span>
                </h3>
                <div className="space-y-2.5">
                  {group.items.map((d) => (
                    <DimensionRow key={d.key} dim={d} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Forces — partie quick, toujours présentes dès le 1er appel Claude */}
      {debrief.strengths && debrief.strengths.length > 0 && (
        <div className="rounded-xl border bg-white p-6">
          <SectionHeader
            icon={<TrendingUp className="h-5 w-5 text-white" />}
            color="#7CBF94"
            title={t("salesCoach.detail.yourStrengths", { count: debrief.strengths.length })}
          />
          <div className="space-y-3">
            {debrief.strengths.map((s, i) => (
              <div key={i} className="p-4 rounded-lg bg-gray-50">
                <div className="font-semibold mb-2" style={{ color: "#1E7A3C" }}>{i + 1}. {s.title}</div>
                <div className="italic text-sm mb-2 text-gray-600">« {s.citation} »</div>
                <div className="text-sm" style={{ color: "#384872" }}>{s.explanation}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Axes d'amélioration — partie quick */}
      {debrief.improvements && debrief.improvements.length > 0 && (
        <div className="rounded-xl border bg-white p-6">
          <SectionHeader
            icon={<Lightbulb className="h-5 w-5 text-white" />}
            color="#F6C847"
            title={t("salesCoach.detail.yourImprovements", { count: debrief.improvements.length })}
          />
          <div className="space-y-3">
            {debrief.improvements.map((imp, i) => {
              const cfg = IMPACT_CONFIG[imp.impact] || IMPACT_CONFIG.medium;
              return (
                <div key={i} className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <div className="font-semibold" style={{ color: cfg.color }}>{i + 1}. {imp.title}</div>
                    <Badge variant="outline" style={{ color: cfg.color, borderColor: cfg.color }}>{t("salesCoach.detail.impactLabel", { level: t(cfg.labelKey) })}</Badge>
                  </div>
                  <div className="mt-2 mb-1 text-xs font-semibold uppercase" style={{ color: "#4B5573" }}>{t("salesCoach.detail.whatYouSaid")}</div>
                  <div className="italic text-sm mb-3" style={{ color: "#4B5573" }}>« {imp.citation} »</div>
                  <div className="mb-1 text-xs font-semibold uppercase" style={{ color: "#4B5573" }}>{t("salesCoach.detail.whatYouCouldHaveSaid")}</div>
                  <div className="text-sm font-medium mb-3" style={{ color: "#384872" }}>« {imp.suggestedReformulation} »</div>
                  {/* Couture Pilier 1 ↔ Pilier 2 : dialog "Préparer ma simulation" préchargé sur l'axe cliqué */}
                  {onStartTrainingForKey && debrief.recommendedSimulator && (
                    <button
                      onClick={() => setTrainingDialogCtx({
                        simulatorKey: debrief.recommendedSimulator.simulatorKey,
                        simulatorLabel: SIMULATOR_LABELS[debrief.recommendedSimulator.simulatorKey] ?? debrief.recommendedSimulator.simulatorKey,
                        axeTitle: imp.title,
                        citation: imp.citation,
                        suggestedReformulation: imp.suggestedReformulation,
                        contextNotes: session.contextNotes ?? undefined,
                        scoreGlobal: debrief.scoreGlobal,
                        prospectName: session.contextProspectName,
                        prospectCompany: session.contextProspectCompany,
                    realCallSessionId: session.id,
                      })}
                      className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg border border-[#988EF2] bg-white hover:bg-[#F8F7FF] transition-colors min-h-[40px]"
                      style={{ color: "#5A4BBE" }}
                    >
                      <Mic className="h-4 w-4" />
                      {t("salesCoach.detail.trainOnThisPoint")}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PR3 — Sections spécifiques par contextType.
          Produites par analyzeContextSpecific (PR2). Rendu typé par type :
          cold_call → 3 premières secondes + résistances + engagement ; discovery → SPIN/BANT/iceberg ;
          pitch → multi-interlocuteurs ; négo → mouvements ; CS → expansion/churn ;
          qualif → table MEDDIC. Affiché juste après les axes d'amélioration pour rester
          dans la zone d'attention principale du commercial. */}
      {(debrief as { contextSpecific?: Parameters<typeof ContextSpecificSection>[0]["contextSpecific"] }).contextSpecific && (
        <ContextSpecificSection contextSpecific={(debrief as { contextSpecific?: Parameters<typeof ContextSpecificSection>[0]["contextSpecific"] }).contextSpecific} />
      )}

      {/* Objections — partie advanced */}
      {debrief.objections && debrief.objections.length > 0 && (
        <CollapsibleSection
          title={t("salesCoach.detail.objectionsDetected")}
          icon={<MessageSquare className="h-5 w-5" />}
          iconColor="#384872"
          badge={`${debrief.objections.length}`}
          summary={buildObjectionsSummary(debrief.objections, t)}
          defaultOpen={debrief.objections.some((o) => o.handling !== "well_handled")}
        >
          <div className="space-y-3">
            {debrief.objections.map((o, i) => {
              const cfg = HANDLING_CONFIG[o.handling];
              return (
                <div key={i} className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-start justify-between mb-2 gap-3">
                    <div className="italic text-sm flex-1 text-gray-600">« {o.objection} »</div>
                    <Badge variant="outline" style={{ color: cfg.color, borderColor: cfg.color }}>{t(cfg.labelKey)}</Badge>
                  </div>
                  {o.handlingCitation && (
                    <div className="text-sm mt-2" style={{ color: "#384872" }}>
                      <span className="font-semibold">{t("salesCoach.detail.yourResponse")}</span> « {o.handlingCitation} »
                    </div>
                  )}
                  {o.suggestion && (
                    <div className="text-sm mt-2" style={{ color: "#384872" }}>
                      <span className="font-semibold">{t("salesCoach.detail.suggestionLabel")}</span> {o.suggestion}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CollapsibleSection>
      )}

      {/* Signaux d'achat & risque — partie advanced */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {debrief.buyingSignals && debrief.buyingSignals.length > 0 && (
          <CollapsibleSection
            title={t("salesCoach.detail.buyingSignals")}
            icon={<TrendingUp className="h-5 w-5" />}
            iconColor="#1E7A3C"
            badge={`${debrief.buyingSignals.length}`}
            summary={t("salesCoach.detail.buyingSignalsSummary", { count: debrief.buyingSignals.length })}
            defaultOpen
          >
            <div className="space-y-3">
              {debrief.buyingSignals.map((s, i) => (
                <div key={i} className="text-sm">
                  <div className="italic mb-1" style={{ color: "#4B5573" }}>
                    « {s.citation} » {s.timestamp && <span className="text-xs" style={{ color: "#6B7280" }}>[{s.timestamp}]</span>}
                  </div>
                  <div style={{ color: "#384872" }}>→ {s.interpretation}</div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {debrief.riskSignals && debrief.riskSignals.length > 0 && (
          <CollapsibleSection
            title={t("salesCoach.detail.riskSignals")}
            icon={<AlertTriangle className="h-5 w-5" />}
            iconColor="#C0423F"
            badge={`${debrief.riskSignals.length}`}
            summary={debrief.riskSignals.some((s) => s.severity === "high") ? t("salesCoach.detail.riskSignalsStrongToHandle") : t("salesCoach.detail.buyingSignalsSummary", { count: debrief.riskSignals.length })}
            defaultOpen
          >
            <div className="space-y-3">
              {debrief.riskSignals.map((s, i) => (
                <div key={i} className="text-sm">
                  <div className="italic mb-1" style={{ color: "#4B5573" }}>
                    « {s.citation} » {s.timestamp && <span className="text-xs" style={{ color: "#6B7280" }}>[{s.timestamp}]</span>}
                  </div>
                  <div style={{ color: "#384872" }}>→ {s.interpretation}</div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}
      </div>

      {/* Méthodologie — accordéon */}
      {debrief.methodologyCoverage && debrief.methodologyCoverage.elements && debrief.methodologyCoverage.elements.length > 0 && (
        <CollapsibleSection
          title={t("salesCoach.detail.methodologyCoverage", { framework: debrief.methodologyCoverage.framework })}
          icon={<Target className="h-5 w-5" />}
          iconColor="#384872"
          badge={`${debrief.methodologyCoverage.elements.filter((e) => e.covered).length}/${debrief.methodologyCoverage.elements.length}`}
          summary={t("salesCoach.detail.methodologyCoverageSummary", { count: debrief.methodologyCoverage.elements.filter((e) => e.covered).length, covered: debrief.methodologyCoverage.elements.filter((e) => e.covered).length, total: debrief.methodologyCoverage.elements.length })}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {debrief.methodologyCoverage.elements.map((el) => (
              <div key={el.key} className="p-3 rounded-lg flex items-center gap-2" style={{ background: el.covered ? "#F0FDF4" : "#FFF5F5" }}>
                {el.covered ? (
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0" style={{ color: "#1E7A3C" }} />
                ) : (
                  <XCircle className="h-5 w-5 flex-shrink-0" style={{ color: "#C0423F" }} />
                )}
                <span className="text-sm font-medium" style={{ color: el.covered ? "#1E7A3C" : "#C0423F" }}>
                  {el.label}
                </span>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Next step — accordéon */}
      {debrief.nextStep && (
        <CollapsibleSection
          title={t("salesCoach.detail.nextStep")}
          icon={<Target className="h-5 w-5" />}
          iconColor="#384872"
          summary={buildNextStepSummary(debrief.nextStep, t)}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <NextStepIndicator label={t("salesCoach.detail.nextStepAnnounced")} value={debrief.nextStep.announced} />
            <NextStepIndicator label={t("salesCoach.detail.nextStepAccepted")} value={debrief.nextStep.accepted} />
            <NextStepIndicator label={t("salesCoach.detail.nextStepDated")} value={debrief.nextStep.dated} />
            <NextStepIndicator label={t("salesCoach.detail.nextStepMutualCommitment")} value={debrief.nextStep.mutualCommitment} />
          </div>
          {debrief.nextStep.citation && (
            <p className="text-sm italic mb-2 text-gray-600">« {debrief.nextStep.citation} »</p>
          )}
          {debrief.nextStep.suggestion && (
            <div className="mt-3 p-3 rounded-lg bg-gray-50">
              <div className="text-xs font-semibold uppercase mb-1 text-gray-500">{t("salesCoach.detail.suggestion")}</div>
              <p className="text-sm" style={{ color: "#384872" }}>{debrief.nextStep.suggestion}</p>
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* Pont RDV réel → entraînement (Étape 7) : carte violette en pied de débrief.
          Réutilise la plomberie de lancement existante (setTrainingDialogCtx), en
          orientant le focus sur le critère le plus faible du vrai RDV. */}
      {bridge && onStartTrainingForKey && debrief.recommendedSimulator && (
        <RealMeetingBridgeCard
          card={bridge}
          lang={i18n.language}
          onPrimary={() => setTrainingDialogCtx({
            simulatorKey: debrief.recommendedSimulator.simulatorKey,
            simulatorLabel: SIMULATOR_LABELS[debrief.recommendedSimulator.simulatorKey] ?? debrief.recommendedSimulator.simulatorKey,
            axeTitle: bridge.weakest ? pickScoringLabel(bridge.weakest, i18n.language) : t("salesCoach.detail.iaRecommendation"),
            contextNotes: (session.contextNotes ?? undefined) || debrief.recommendedSimulator.reason,
            scoreGlobal: debrief.scoreGlobal,
            prospectName: session.contextProspectName,
            prospectCompany: session.contextProspectCompany,
            realCallSessionId: session.id,
          })}
          onSecondary={onPrepareMeeting}
        />
      )}

        </TabsContent>

        {/* Tab Transcription
            V3 : si on a audio + utterances avec words → player synchro
            (highlight mot courant + click pour seek). Sinon : transcript brut. */}
        <TabsContent value="transcript" className="mt-6">
          {/* Enregistrement purgé après 90 jours (engagement du Dossier de Confiance) : sans ce
              mot, le lecteur audio disparaît d'un débrief qui en avait un, et c'est pris pour
              une panne. La transcription, elle, est toujours là — on le dit. */}
          {enregistrementPurge(session) && (
            <div
              className="mb-4 rounded-md border p-3 text-sm flex items-start gap-2"
              style={{ background: "#F3F4F6", borderColor: "#E5E7EB", color: "#384872" }}
            >
              <Clock className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{t("salesCoach.detail.audioPurged", { days: AUDIO_RETENTION_DAYS })}</span>
            </div>
          )}
          {session.audioStorageKey
            && Array.isArray(session.transcriptUtterances)
            && session.transcriptUtterances.length > 0
            && session.transcriptUtterances.some((u) => Array.isArray(u.words) && u.words.length > 0)
            ? <AudioSyncedTranscriptView sessionId={session.id} utterances={session.transcriptUtterances} messageRegister={messageRegister} />
            : <TranscriptView text={session.transcriptText} speakerMapping={(session as { speakerMapping?: { commercialSpeaksFirst?: boolean } | null }).speakerMapping} />}
        </TabsContent>
      </Tabs>

      {/* Sticky bottom CTA mobile — 2026-05-15. Sur smartphone, après scroll
          long du débrief, l'action "Lancer ma simulation" doit rester
          accessible dans la zone du pouce. Masqué sur md+ car le CTA en haut
          de la tab Analyse suffit en desktop.
          Note : pr-16 sur le bouton pour ne pas passer SOUS le widget
          Zoho Desk présent en bas-droite (~70px de large). */}
      {onStartTrainingForKey && debrief.recommendedSimulator && (
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]"
          style={{ borderColor: "#E5E7EB", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
        >
          <button
            onClick={() => setTrainingDialogCtx({
              simulatorKey: debrief.recommendedSimulator.simulatorKey,
              simulatorLabel: SIMULATOR_LABELS[debrief.recommendedSimulator.simulatorKey] ?? debrief.recommendedSimulator.simulatorKey,
              axeTitle: t("salesCoach.detail.iaRecommendation"),
              contextNotes: (session.contextNotes ?? undefined) || debrief.recommendedSimulator.reason,
              scoreGlobal: debrief.scoreGlobal,
              prospectName: session.contextProspectName,
              prospectCompany: session.contextProspectCompany,
                    realCallSessionId: session.id,
            })}
            className="w-full inline-flex items-center justify-center gap-2 text-base font-semibold h-12 rounded-lg shadow-md hover:shadow-lg transition-shadow text-white select-none pr-16"
            style={{ backgroundColor: "#5A4BBE" }}
            aria-label={t("salesCoach.detail.launchSimulationAria")}
          >
            <Mic className="h-5 w-5" />
            {t("salesCoach.detail.trainOnThisSituation")}
          </button>
        </div>
      )}

      {/* Dialog "Préparer ma simulation" — flow coaching → entraînement.
          Stocke le contexte (offer, persona, focus, difficulty) en localStorage
          pour transmission à TrainingPage qui le passera à N8N lors de la
          génération du scénario. */}
      <PrepareTrainingFromCoachDialog
        open={!!trainingDialogCtx}
        context={trainingDialogCtx}
        onClose={() => setTrainingDialogCtx(null)}
        onLaunch={async (payload) => {
          // Chantier 2.2 — on persiste realCallSessionId pour que App.tsx
          // déclenche la création du cas privé en BDD via coaching-brief IA.
          // Détails B2B Sales (industry/companySize/customIndustry) inclus si
          // la persona choisie est B2B Sales — sinon undefined, ignoré côté backend.
          localStorage.setItem("bizcoach_pending_coaching_context", JSON.stringify({
            simulatorKey: payload.simulatorKey,
            offerId: payload.offerId,
            personaId: payload.personaId,
            focus: payload.focus,
            difficulty: payload.difficulty,
            industry: payload.industry,
            companySize: payload.companySize,
            customIndustry: payload.customIndustry,
            contextId: payload.contextId,
            realCallSessionId: trainingDialogCtx?.realCallSessionId,
            createdAt: Date.now(),
          }));
          setTrainingDialogCtx(null);
          onStartTrainingForKey?.(payload.simulatorKey);
        }}
      />
    </div>
  );
}

// ============================================================
// Sous-composants : Header prospect, KPI cards, Transcription
// ============================================================

function ProspectHeader({ session }: { session: RealCallSessionFull }) {
  const { t, i18n } = useTranslation();
  const name = session.contextProspectName || t("salesCoach.detail.prospectAnonymous");
  const company = session.contextProspectCompany;

  return (
    <div className="rounded-xl border bg-white p-5">
      <div className="flex items-start gap-4 flex-wrap">
        {/* Icône Sales Coach Réel — cohérence avec la tuile dashboard */}
        <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "#988EF2" }}>
          <Mic className="h-7 w-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{name}</h1>
            {session.isPrivate && (
              <Badge variant="outline" className="gap-1">
                <Lock className="h-3 w-3" /> {t("salesCoach.detail.badgePrivate")}
              </Badge>
            )}
            {session.keepIndefinitely && (
              <Badge variant="outline" className="gap-1">
                <Bookmark className="h-3 w-3" /> {t("salesCoach.detail.badgeKept")}
              </Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            {company ? <span>{company} · </span> : null}
            <span>{t(CONTEXT_LABEL_KEYS[session.contextType])}</span>
            <span> · {formatDate(session.createdAt, i18n.language)}</span>
          </p>
          {session.contextNotes && (
            <p className="mt-2 text-sm italic text-gray-600">
              « {session.contextNotes} »
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCards({ session, debrief, tier }: {
  session: RealCallSessionFull;
  debrief: NonNullable<RealCallSessionFull["debriefJson"]>;
  tier: { labelKey: string; bg: string; text: string; border: string };
}) {
  const { t } = useTranslation();
  // Stats objectives calculées depuis le transcript
  const stats = useMemo(() => computeTranscriptStats(session.transcriptText, session as { speakerMapping?: { commercialSpeaksFirst?: boolean } | null }), [session.transcriptText, session]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {/* Score global */}
      <KpiTile
        label={t("salesCoach.detail.kpiScoreGlobal")}
        value={
          <span style={{ color: tier.border, fontFamily: "Sora, sans-serif" }}>
            {debrief.scoreGlobal}<span className="text-base text-gray-400 font-normal">/100</span>
          </span>
        }
        footer={<Badge style={{ background: tier.bg, color: tier.text }}>{t(tier.labelKey)}</Badge>}
      />

      {/* Durée estimée */}
      <KpiTile
        icon={<Clock className="h-3.5 w-3.5" />}
        label={t("salesCoach.detail.kpiDurationEstimated")}
        value={stats.durationDisplay}
        footer={<>{t("salesCoach.detail.kpiDurationCalc", { count: stats.totalWords })}</>}
      />

      {/* Vitesse de parole */}
      <KpiTile
        icon={<Activity className="h-3.5 w-3.5" />}
        label={t("salesCoach.detail.kpiSpeechRate")}
        value={
          <>
            {stats.wordsPerMinute ?? "—"}<span className="text-sm text-gray-400 font-normal"> {t("salesCoach.detail.kpiSpeechRateUnit")}</span>
          </>
        }
        footer={stats.wordsPerMinute ? t("salesCoach.detail.kpiSpeechRateRef") : t("salesCoach.detail.kpiSpeechRateNA")}
      />

      {/* Ratio parole/écoute */}
      <KpiTile
        icon={<Type className="h-3.5 w-3.5" />}
        label={t("salesCoach.detail.kpiTalkListen")}
        value={stats.salesRatioPct !== null ? (
          <>
            <span style={{ color: stats.salesRatioPct > 50 ? "#C0423F" : "#1E7A3C" }}>{stats.salesRatioPct}%</span>
            <span className="text-base text-gray-400 font-normal"> / {100 - stats.salesRatioPct}%</span>
          </>
        ) : (
          <span className="text-base text-gray-400 font-normal">{t("salesCoach.detail.kpiTalkListenNA")}</span>
        )}
        footer={stats.salesRatioPct !== null ? (
          <>
            <div className="h-1.5 rounded-full overflow-hidden bg-gray-100 mb-1">
              <div className="h-full" style={{ width: `${stats.salesRatioPct}%`, background: stats.salesRatioPct > 50 ? "#F49390" : "#7CBF94" }} />
            </div>
            <span>{t("salesCoach.detail.kpiSpeakersLegend")}</span>
          </>
        ) : t("salesCoach.detail.kpiSpeakersUnknown")}
      />
    </div>
  );
}

// Tuile KPI cohérente avec le style BizCoach (rounded-xl, blanc, gris discret)
function KpiTile({ icon, label, value, footer }: { icon?: React.ReactNode; label: string; value: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="text-xs text-gray-500 flex items-center gap-1.5 mb-1.5">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-2xl font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
        {value}
      </div>
      {footer && (
        <div className="text-xs text-gray-500 mt-2">{footer}</div>
      )}
    </div>
  );
}

// Affichage du transcript brut avec parsing simple des locuteurs.
// `speakerMapping` peut être de la forme { commercialSpeaksFirst: boolean } —
// stocké en BDD via le formulaire de création de session. Sert au mapping
// commercial/prospect quand les labels ne sont pas explicites.
function TranscriptView({ text, speakerMapping }: { text: string | null; speakerMapping?: { commercialSpeaksFirst?: boolean } | null }) {
  const { t } = useTranslation();
  if (!text) {
    return (
      <Card className="p-6">
        <p style={{ color: "#9CA3AF" }}>{t("salesCoach.detail.transcriptUnavailable")}</p>
      </Card>
    );
  }

  const lines = useMemo(() => parseTranscriptLines(text, speakerMapping), [text, speakerMapping]);

  return (
    <Card className="p-6">
      <div className="text-xs uppercase tracking-wide mb-3" style={{ color: "#9CA3AF" }}>
        {t("salesCoach.detail.transcriptRawHeader", { count: text.split(/\s+/).length })}
      </div>
      <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
        {lines.map((line, i) => {
          const isCommercial = line.speaker === "commercial";
          const isProspect = line.speaker === "prospect";
          const speakerColor = isCommercial ? "#384872" : isProspect ? "#5A4BBE" : "#6B7280";
          const bg = isCommercial ? "#F0F7FF" : isProspect ? "#F8F7FF" : "transparent";
          return (
            <div key={i} className="flex gap-3 p-2 rounded" style={{ background: bg }}>
              {line.speaker !== "unknown" && (
                <div className="flex-shrink-0 text-xs font-bold uppercase tracking-wider w-20" style={{ color: speakerColor }}>
                  {isCommercial ? t("salesCoach.detail.speakerMe") : isProspect ? t("salesCoach.detail.speakerProspect") : line.rawLabel || ""}
                </div>
              )}
              <div className="text-sm leading-relaxed flex-1" style={{ color: line.speaker === "unknown" ? "#4B5573" : "#384872" }}>
                {line.content}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ============================================================
// V3 — AudioSyncedTranscriptView : player audio + transcript clickable.
// - Player HTML5 audio avec contrôles natifs
// - Highlight du mot courant pendant la lecture (re-render sur timeupdate)
// - Click sur un mot → seek + play
// - Auto-scroll vers le mot courant si on quitte le viewport
// - Fallback : si pas de words[], affiche le texte sans interactivité
// ============================================================
type Utterance = NonNullable<RealCallSessionFull["transcriptUtterances"]>[number];

type Note = Awaited<ReturnType<typeof salesCoachApi.listSessionNotes>>[number];

function AudioSyncedTranscriptView({ sessionId, utterances, messageRegister }: { sessionId: string; utterances: Utterance[]; messageRegister?: "tu" | "vous" }) {
  const { t, i18n } = useTranslation();
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // V3 — notes au timestamp
  const [notes, setNotes] = useState<Note[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteDraft, setNoteDraft] = useState<{ startSec: number; body: string; kind: "self" | "manager_note" } | null>(null);
  const [noteSaving, setNoteSaving] = useState(false);

  // V3 — clips audio partageables
  const [clips, setClips] = useState<Awaited<ReturnType<typeof salesCoachApi.listSessionClips>>>([]);
  const [clipDraft, setClipDraft] = useState<{ startSec: number; endSec: number; title: string; note: string } | null>(null);
  const [clipSaving, setClipSaving] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  // Charge les notes au mount
  useEffect(() => {
    setNotesLoading(true);
    salesCoachApi.listSessionNotes(sessionId)
      .then(setNotes)
      .catch(() => setNotes([]))
      .finally(() => setNotesLoading(false));
    salesCoachApi.listSessionClips(sessionId)
      .then(setClips)
      .catch(() => setClips([]));
  }, [sessionId]);

  async function saveClip() {
    if (!clipDraft) return;
    if (clipDraft.endSec <= clipDraft.startSec) {
      alert(t("salesCoach.detail.alertEndAfterStart"));
      return;
    }
    setClipSaving(true);
    try {
      const created = await salesCoachApi.createSessionClip(sessionId, {
        startSec: clipDraft.startSec,
        endSec: clipDraft.endSec,
        title: clipDraft.title.trim() || undefined,
        note: clipDraft.note.trim() || undefined,
      });
      setClips((prev) => [...prev, created].sort((a, b) => a.startSec - b.startSec));
      setClipDraft(null);
      // Copie automatique du lien dans le presse-papier après création
      const fullUrl = `${window.location.origin}${created.shareUrlPath}`;
      try { await navigator.clipboard.writeText(fullUrl); setCopiedToken(created.shareToken); setTimeout(() => setCopiedToken(null), 2500); } catch { /* clipboard non dispo */ }
    } catch (err) {
      alert(err instanceof Error ? err.message : t("salesCoach.detail.errorGeneric"));
    } finally {
      setClipSaving(false);
    }
  }

  async function removeClip(clipId: string) {
    if (!confirm(t("salesCoach.detail.confirmDeleteClip"))) return;
    try {
      await salesCoachApi.deleteClip(clipId);
      setClips((prev) => prev.filter((c) => c.id !== clipId));
    } catch (err) {
      alert(err instanceof Error ? err.message : t("salesCoach.detail.errorGeneric"));
    }
  }

  async function copyShareLink(shareUrlPath: string, token: string) {
    const fullUrl = `${window.location.origin}${shareUrlPath}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2500);
    } catch {
      prompt("Copier le lien :", fullUrl);
    }
  }

  async function saveNote() {
    if (!noteDraft || !noteDraft.body.trim()) return;
    setNoteSaving(true);
    try {
      const created = await salesCoachApi.createSessionNote(sessionId, {
        startSec: noteDraft.startSec,
        body: noteDraft.body.trim(),
        kind: noteDraft.kind,
      });
      setNotes((prev) => [...prev, created as Note].sort((a, b) => a.startSec - b.startSec));
      setNoteDraft(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : t("salesCoach.detail.errorGeneric"));
    } finally {
      setNoteSaving(false);
    }
  }

  async function removeNote(noteId: string) {
    if (!confirm(t("salesCoach.detail.confirmDeleteNote"))) return;
    try {
      await salesCoachApi.deleteSessionNote(noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (err) {
      alert(err instanceof Error ? err.message : t("salesCoach.detail.errorGeneric"));
    }
  }

  // Charge l'URL signée R2 (15 min). Re-fetch si on dépasse 14 min pour
  // gérer les longues écoutes sans expiration silencieuse.
  useEffect(() => {
    let cancelled = false;
    setAudioUrl(null);
    setUrlError(null);
    salesCoachApi.getSessionAudioUrl(sessionId)
      .then(({ url }) => { if (!cancelled) setAudioUrl(url); })
      .catch((err) => { if (!cancelled) setUrlError(err.message ?? t("salesCoach.detail.audioUnavailable")); });
    return () => { cancelled = true; };
  }, [sessionId]);

  function seekTo(sec: number) {
    if (!audioRef.current) return;
    audioRef.current.currentTime = sec;
    audioRef.current.play().catch(() => { /* ignore autoplay blocks */ });
  }

  // Auto-scroll vers le mot courant — seulement si on est en lecture
  // (sinon ça scrolle sans cesse pendant la navigation manuelle)
  useEffect(() => {
    if (!containerRef.current || !audioRef.current || audioRef.current.paused) return;
    const current = containerRef.current.querySelector<HTMLElement>(".word-current");
    if (!current) return;
    const rect = current.getBoundingClientRect();
    const cRect = containerRef.current.getBoundingClientRect();
    if (rect.top < cRect.top + 50 || rect.bottom > cRect.bottom - 50) {
      current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [currentTime]);

  return (
    <Card className="p-4 md:p-6 space-y-4">
      {/* Player audio */}
      <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: "#F8F7FF" }}>
        <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "#5A4BBE" }}>
          <Volume2 className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          {urlError && (
            <div className="text-xs flex items-center gap-1.5" style={{ color: "#C0423F" }}>
              <AlertCircle className="h-3.5 w-3.5" /> {urlError}
            </div>
          )}
          {!audioUrl && !urlError && (
            <div className="text-xs flex items-center gap-1.5" style={{ color: "#6B7280" }}>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("salesCoach.detail.audioLoading")}
            </div>
          )}
          {audioUrl && (
            <audio
              ref={audioRef}
              src={audioUrl}
              controls
              preload="metadata"
              className="w-full"
              onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
              onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
            />
          )}
        </div>
        {duration > 0 && (
          <div className="text-xs font-mono shrink-0" style={{ color: "#5A4BBE" }}>
            {formatSec(currentTime)} / {formatSec(duration)}
          </div>
        )}
      </div>

      {/* Hint */}
      <div className="text-xs flex items-center gap-1.5" style={{ color: "#6B7280" }}>
        <Play className="h-3 w-3" />
        {t("salesCoach.detail.clickWordHint")}
      </div>

      {/* V3 — Bandeau notes : compteur + scroll vers liste */}
      {!notesLoading && notes.length > 0 && (
        <div className="rounded-lg p-2.5 flex items-center gap-2 text-xs" style={{ background: "#FFFBEB", color: "#854D0E" }}>
          <StickyNote className="h-3.5 w-3.5 shrink-0" />
          <span className="font-semibold">{t("salesCoach.detail.notesBadge", { count: notes.length })}</span>
          <span style={{ color: "#92400E" }}>·</span>
          <span>{t("salesCoach.detail.notesCoachingCount", { count: notes.filter((n) => n.kind === "manager_note").length })}</span>
          <a href="#notes-list" className="ml-auto underline" style={{ color: "#854D0E" }}>{t("salesCoach.detail.viewNotes")}</a>
        </div>
      )}

      {/* Transcript synchro */}
      <div ref={containerRef} className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
        {utterances.map((u, i) => {
          const isCommercial = u.role === "commercial";
          const isProspect = u.role === "prospect";
          const bg = isCommercial ? "#F0F7FF" : isProspect ? "#F8F7FF" : "transparent";
          const speakerColor = isCommercial ? "#384872" : isProspect ? "#5A4BBE" : "#6B7280";
          return (
            <div key={i} className="flex gap-3 p-2.5 rounded-lg" style={{ background: bg }}>
              {/* Sidebar gauche : 24 px width sur mobile (au lieu de 20) + boutons
                  agrandis avec icônes 3.5×3.5 pour respect des touch targets. */}
              <div className="flex-shrink-0 w-24 flex flex-col gap-1.5">
                <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: speakerColor }}>
                  {isCommercial ? t("salesCoach.detail.speakerMe") : isProspect ? t("salesCoach.detail.speakerProspect") : "—"}
                </div>
                <button
                  onClick={() => seekTo(u.startSec)}
                  className="text-xs font-mono hover:underline text-left py-0.5 select-none"
                  style={{ color: "#6B7280" }}
                  title={t("salesCoach.detail.goToMoment")}
                  aria-label={t("salesCoach.detail.goToMomentAt", { time: formatSec(u.startSec) })}
                >
                  {formatSec(u.startSec)}
                </button>
                <button
                  onClick={() => setNoteDraft({ startSec: u.startSec, body: "", kind: "self" })}
                  className="text-xs hover:underline flex items-center gap-1 text-left py-1 px-1 -mx-1 rounded select-none"
                  style={{ color: "#5A4BBE" }}
                  title={t("salesCoach.detail.addNote")}
                  aria-label={t("salesCoach.detail.addNote")}
                >
                  <Plus className="h-3.5 w-3.5 shrink-0" /> {t("salesCoach.detail.noteLabel")}
                </button>
                <button
                  onClick={() => setClipDraft({
                    startSec: u.startSec,
                    endSec: Math.min(u.endSec + 5, u.startSec + 60),
                    title: "",
                    note: "",
                  })}
                  className="text-xs hover:underline flex items-center gap-1 text-left py-1 px-1 -mx-1 rounded select-none"
                  style={{ color: "#0EA5E9" }}
                  title={t("salesCoach.detail.shareClipTitle")}
                  aria-label={t("salesCoach.detail.shareClipAria")}
                >
                  <Share2 className="h-3.5 w-3.5 shrink-0" /> {t("salesCoach.detail.clipLabel")}
                </button>
              </div>
              {/* Mots clickables : leading + padding élargis pour offrir
                  un touch target raisonnable sur mobile (≥32px height par mot).
                  text-sm sur mobile reste lisible, on évite text-xs trop petit. */}
              <div className="text-sm leading-7 flex-1 flex flex-wrap gap-x-0.5 gap-y-1" style={{ color: "#384872" }}>
                {Array.isArray(u.words) && u.words.length > 0
                  ? u.words.map((w, j) => {
                      const isCurrent = currentTime >= w.startSec && currentTime < w.endSec;
                      return (
                        <span
                          key={j}
                          role="button"
                          tabIndex={0}
                          onClick={() => seekTo(w.startSec)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); seekTo(w.startSec); } }}
                          className={`cursor-pointer hover:bg-yellow-100 rounded px-1 py-0.5 transition-colors select-none ${isCurrent ? "word-current" : ""}`}
                          style={isCurrent ? { background: "#FEF08A", fontWeight: 600 } : undefined}
                          aria-label={t("salesCoach.detail.goToWordAria", { word: w.word, time: formatSec(w.startSec) })}
                        >
                          {w.word}
                        </span>
                      );
                    })
                  : <span>{u.text}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* V3 — Liste des notes */}
      {notes.length > 0 && (
        <div id="notes-list" className="border-t pt-4 mt-2" style={{ borderColor: "#E5E7EB" }}>
          <div className="flex items-center gap-2 mb-3">
            <StickyNote className="h-4 w-4" style={{ color: "#854D0E" }} />
            <h3 className="font-semibold text-sm" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
              {t("salesCoach.detail.notesListTitle", { count: notes.length })}
            </h3>
          </div>
          <div className="space-y-2">
            {notes.map((n) => (
              <div
                key={n.id}
                className="rounded-lg p-3 flex items-start gap-3"
                style={{
                  background: n.kind === "manager_note" ? "#F3F0FF" : "#FFFBEB",
                  borderLeft: `3px solid ${n.kind === "manager_note" ? "#5A4BBE" : "#F6C847"}`,
                }}
              >
                <button
                  onClick={() => seekTo(n.startSec)}
                  className="text-[10px] font-mono shrink-0 hover:underline"
                  style={{ color: n.kind === "manager_note" ? "#5A4BBE" : "#854D0E" }}
                  title={t("salesCoach.detail.goToMoment")}
                >
                  {formatSec(n.startSec)}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-sm whitespace-pre-wrap" style={{ color: "#384872" }}>{n.body}</div>
                  <div className="text-[10px] mt-1 flex items-center gap-2" style={{ color: "#6B7280" }}>
                    <span>{[n.authorFirstName, n.authorLastName].filter(Boolean).join(" ") || t("salesCoach.detail.authorAnonymous")}</span>
                    {n.kind === "manager_note" && (
                      <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase" style={{ background: "#988EF2", color: "white" }}>
                        {t("salesCoach.detail.coachingBadge")}
                      </span>
                    )}
                    <span>· {new Date(n.createdAt).toLocaleString(i18n.language === "fr" ? "fr-FR" : "en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                </div>
                {n.canEdit && (
                  <button
                    onClick={() => removeNote(n.id)}
                    className="p-1 rounded-full hover:bg-red-50"
                    title={t("salesCoach.detail.deleteNoteTitle")}
                  >
                    <Trash2 className="h-3.5 w-3.5" style={{ color: "#C0423F" }} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* V3 — Liste des clips partagés */}
      {clips.length > 0 && (
        <div id="clips-list" className="border-t pt-4" style={{ borderColor: "#E5E7EB" }}>
          <div className="flex items-center gap-2 mb-3">
            <Scissors className="h-4 w-4" style={{ color: "#0EA5E9" }} />
            <h3 className="font-semibold text-sm" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
              {t("salesCoach.detail.sharedClipsTitle", { count: clips.length })}
            </h3>
          </div>
          <div className="space-y-2">
            {clips.map((c) => {
              const isCopied = copiedToken === c.shareToken;
              const duration = Math.round(c.endSec - c.startSec);
              const fullUrl = typeof window !== "undefined" ? `${window.location.origin}${c.shareUrlPath}` : c.shareUrlPath;
              return (
                <div key={c.id} className="rounded-lg p-3 flex items-start gap-3" style={{ background: "#F0F9FF", borderLeft: "3px solid #0EA5E9" }}>
                  <button
                    onClick={() => seekTo(c.startSec)}
                    className="text-[10px] font-mono shrink-0 hover:underline"
                    style={{ color: "#0EA5E9" }}
                    title={t("salesCoach.detail.goToMoment")}
                  >
                    {formatSec(c.startSec)}–{formatSec(c.endSec)}
                  </button>
                  <div className="flex-1 min-w-0">
                    {c.title && <div className="text-sm font-semibold" style={{ color: "#384872" }}>{c.title}</div>}
                    {c.note && <div className="text-xs mt-0.5 whitespace-pre-wrap" style={{ color: "#4B5573" }}>{c.note}</div>}
                    <div className="flex items-center gap-2 mt-1.5">
                      <button
                        onClick={() => copyShareLink(c.shareUrlPath, c.shareToken)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md transition-colors"
                        style={{ background: isCopied ? "#D7F0E0" : "#DDEEFF", color: isCopied ? "#1E7A3C" : "#0EA5E9" }}
                      >
                        {isCopied ? <><Check className="h-3 w-3" /> {t("salesCoach.detail.linkCopied")}</> : <><LinkIcon className="h-3 w-3" /> {t("salesCoach.detail.copyLink")}</>}
                      </button>
                      <span className="text-[10px]" style={{ color: "#6B7280" }}>
                        {duration}s · {t("salesCoach.detail.viewCount", { count: c.viewCount })}
                        {c.expiresAt && ` · ${t("salesCoach.detail.clipMetaExpires", { date: new Date(c.expiresAt).toLocaleDateString(i18n.language === "fr" ? "fr-FR" : "en-GB", { day: "2-digit", month: "short" }) })}`}
                      </span>
                    </div>
                    <div className="text-[10px] mt-1 font-mono break-all" style={{ color: "#9CA3AF" }}>{fullUrl}</div>
                  </div>
                  <button
                    onClick={() => removeClip(c.id)}
                    className="p-1 rounded-full hover:bg-red-50 shrink-0"
                    title={t("salesCoach.detail.deleteClipTitle")}
                  >
                    <Trash2 className="h-3.5 w-3.5" style={{ color: "#C0423F" }} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* V3 — Modal d'ajout de clip */}
      {clipDraft && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.4)" }}
          onClick={() => setClipDraft(null)}
        >
          <div className="bg-white rounded-2xl p-5 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                  <Scissors className="h-5 w-5" style={{ color: "#0EA5E9" }} />
                  {t("salesCoach.detail.clipModalTitle")}
                </h3>
                <p className="text-xs mt-0.5" style={{ color: "#6B7280" }}>
                  {t("salesCoach.detail.clipModalSubtitle")}
                </p>
              </div>
              <button onClick={() => setClipDraft(null)} className="p-1 rounded-full hover:bg-gray-100">
                <X className="h-4 w-4" style={{ color: "#6B7280" }} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium" style={{ color: "#4B5573" }}>{t("salesCoach.detail.clipStartSec")}</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.5"
                  min="0"
                  value={clipDraft.startSec}
                  onChange={(e) => setClipDraft({ ...clipDraft, startSec: Number(e.target.value) })}
                  className="w-full mt-1 border rounded-md px-2 py-2 text-sm font-mono"
                  style={{ borderColor: "#D1D5DB" }}
                />
              </div>
              <div>
                <label className="text-xs font-medium" style={{ color: "#4B5573" }}>{t("salesCoach.detail.clipEndSec")}</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.5"
                  min={clipDraft.startSec}
                  value={clipDraft.endSec}
                  onChange={(e) => setClipDraft({ ...clipDraft, endSec: Number(e.target.value) })}
                  className="w-full mt-1 border rounded-md px-2 py-2 text-sm font-mono"
                  style={{ borderColor: "#D1D5DB" }}
                />
              </div>
            </div>
            <div className="text-xs" style={{ color: "#6B7280" }}>
              {t("salesCoach.detail.clipDuration", { seconds: Math.max(0, Math.round(clipDraft.endSec - clipDraft.startSec)) })}
            </div>
            <input
              type="text"
              value={clipDraft.title}
              onChange={(e) => setClipDraft({ ...clipDraft, title: e.target.value })}
              placeholder={t("salesCoach.detail.clipTitlePlaceholder")}
              className="w-full border rounded-md px-3 py-2 text-sm"
              style={{ borderColor: "#D1D5DB" }}
              maxLength={200}
            />
            <textarea
              value={clipDraft.note}
              onChange={(e) => setClipDraft({ ...clipDraft, note: e.target.value })}
              placeholder={t("salesCoach.detail.clipNotePlaceholder")}
              className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px] resize-none"
              style={{ borderColor: "#D1D5DB" }}
              maxLength={1000}
            />
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setClipDraft(null)}
                className="px-3 py-1.5 text-sm rounded-lg border hover:bg-gray-50"
                style={{ borderColor: "#D1D5DB", color: "#384872" }}
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={saveClip}
                disabled={clipSaving || clipDraft.endSec <= clipDraft.startSec}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: "#0EA5E9", color: "white" }}
              >
                {clipSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                <Share2 className="h-3.5 w-3.5" />
                {t("salesCoach.detail.createLink")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* V3 — Modal d'ajout de note */}
      {noteDraft && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.4)" }}
          onClick={() => setNoteDraft(null)}
        >
          <div className="bg-white rounded-2xl p-5 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-bold text-lg" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                  {t("salesCoach.detail.noteModalTitle")}
                </h3>
                <p className="text-xs mt-0.5" style={{ color: "#6B7280" }}>
                  <Trans
                    i18nKey="salesCoach.detail.noteModalSubtitle"
                    values={{ time: formatSec(noteDraft.startSec) }}
                    components={{ 1: <span className="font-mono" /> }}
                  />
                </p>
              </div>
              <button onClick={() => setNoteDraft(null)} className="p-1 rounded-full hover:bg-gray-100">
                <X className="h-4 w-4" style={{ color: "#6B7280" }} />
              </button>
            </div>
            <textarea
              autoFocus
              value={noteDraft.body}
              onChange={(e) => setNoteDraft({ ...noteDraft, body: e.target.value })}
              // Décision Q14 (30/09/2026) : note du manager au commercial = registre de l'ENTREPRISE.
              placeholder={t(coachNotePlaceholderKey(messageRegister ?? companyMessageRegister()))}
              className="w-full border rounded-lg p-3 text-sm min-h-[120px] resize-none focus:outline-none focus:ring-2"
              style={{ borderColor: "#D1D5DB" }}
              maxLength={5000}
            />
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "#384872" }}>
                <input
                  type="checkbox"
                  checked={noteDraft.kind === "manager_note"}
                  onChange={(e) => setNoteDraft({ ...noteDraft, kind: e.target.checked ? "manager_note" : "self" })}
                />
                {t("salesCoach.detail.noteKindCoaching")}
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setNoteDraft(null)}
                  className="px-3 py-1.5 text-sm rounded-lg border hover:bg-gray-50"
                  style={{ borderColor: "#D1D5DB", color: "#384872" }}
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={saveNote}
                  disabled={!noteDraft.body.trim() || noteSaving}
                  className="px-3 py-1.5 text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: "#384872", color: "white" }}
                >
                  {noteSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {t("common.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function formatSec(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ============================================================
// Utilities : parsing transcript et calcul stats
// ============================================================

type ParsedLine = { speaker: "commercial" | "prospect" | "unknown"; rawLabel?: string; content: string };

// V2 fix 2026-05-14 — Parser robuste pour formats variés :
// - [MOI] / [PROSPECT] / **MOI** (préfixes structurés)
// - "MOI 02:30" (label avec timestamp embarqué, type Otter/Modjo)
// - "Speaker 1:" / "Speaker 2:" (format générique transcription)
// - "Amaury Fatoux:" / "Dominique Seguin:" (noms propres Google Meet/Zoom)
// - Lignes timestamp standalone "00:01:20" → ignorées (séparateurs)
//
// Utilise `commercialSpeaksFirst` pour mapper le 1er locuteur vu en commercial
// ou prospect selon ce que l'utilisateur a déclaré au moment de la création.

// Ligne ne contenant qu'un timestamp (MM:SS ou HH:MM:SS) → séparateur, à ignorer
const TIMESTAMP_LINE_REGEX = /^\d{1,2}:\d{2}(:\d{2})?$/;
// Timestamp embarqué en fin de label : "MOI 02:30" → on garde "MOI"
const TRAILING_TIMESTAMP_REGEX = /\s+\d{1,2}[:\.]?\d{0,2}([:\.]\d{1,2})?\s*$/;

function parseTranscriptLines(
  text: string,
  speakerMapping?: { commercialSpeaksFirst?: boolean } | null,
): ParsedLine[] {
  const commercialSpeaksFirst = speakerMapping?.commercialSpeaksFirst !== false; // défaut true

  // Filtre les timestamps standalone (artefact d'export Google Meet / Zoom)
  const lines = text
    .split(/\n+/)
    .map((l) => l.trim())
    .filter((l) => l && !TIMESTAMP_LINE_REGEX.test(l));

  let firstSeenLabel: string | null = null;
  let secondSeenLabel: string | null = null;

  return lines.map((line) => {
    // Patterns acceptés : [LABEL] content · **LABEL** content · LABEL: content
    const match = line.match(/^(?:\[([A-Za-zÀ-ÿ0-9 _\-]{1,40})\]|\*\*([A-Za-zÀ-ÿ0-9 _\-]{1,40})\*\*|([A-Za-zÀ-ÿ0-9 _\-]{1,40}))\s*[:：]\s*(.*)$/);
    if (!match || !(match[1] || match[2] || match[3])) {
      return { speaker: "unknown", content: line };
    }

    // Normalise le label : retire le timestamp trailing (ex: "MOI 02:30" → "MOI")
    const rawLabel = (match[1] || match[2] || match[3] || "").trim();
    const cleanLabel = rawLabel.replace(TRAILING_TIMESTAMP_REGEX, "").trim().toUpperCase();
    const content = (match[4] || "").trim();
    if (!cleanLabel) return { speaker: "unknown", content: line };

    // Mémorise les 2 premiers labels distincts pour mapping commercial/prospect
    if (!firstSeenLabel) firstSeenLabel = cleanLabel;
    else if (cleanLabel !== firstSeenLabel && !secondSeenLabel) secondSeenLabel = cleanLabel;

    // 3 stratégies de mapping, dans l'ordre :
    // 1. Label explicite reconnu (MOI, COMMERCIAL, PROSPECT, CLIENT...) → priorité
    // 2. Premier locuteur vu → commercial ou prospect selon commercialSpeaksFirst
    // 3. Second locuteur vu → l'autre rôle
    let speaker: ParsedLine["speaker"] = "unknown";
    if (COMMERCIAL_LABELS.has(cleanLabel)) speaker = "commercial";
    else if (PROSPECT_LABELS.has(cleanLabel)) speaker = "prospect";
    else if (cleanLabel === firstSeenLabel) speaker = commercialSpeaksFirst ? "commercial" : "prospect";
    else if (cleanLabel === secondSeenLabel) speaker = commercialSpeaksFirst ? "prospect" : "commercial";

    return { speaker, rawLabel: cleanLabel, content: content || line };
  });
}

const COMMERCIAL_LABELS = new Set([
  "MOI", "COMMERCIAL", "VENDEUR", "REP", "SALES", "SDR", "ME",
  "SPEAKER 1", "SPEAKER_1",
]);
const PROSPECT_LABELS = new Set([
  "PROSPECT", "CLIENT", "BUYER", "CUSTOMER", "LEAD",
  "SPEAKER 2", "SPEAKER_2",
]);

type TranscriptStats = {
  totalWords: number;
  durationSec: number;
  durationDisplay: string;
  wordsPerMinute: number | null;
  salesRatioPct: number | null; // null si pas calculable (pas de speakers détectés)
};

function computeTranscriptStats(
  text: string | null,
  session: { speakerMapping?: { commercialSpeaksFirst?: boolean } | { commercial?: string; prospect?: string } | null },
): TranscriptStats {
  if (!text) {
    return { totalWords: 0, durationSec: 0, durationDisplay: "—", wordsPerMinute: null, salesRatioPct: null };
  }

  const totalWords = text.split(/\s+/).filter(Boolean).length;
  // Estimation durée : 140 mots/min en moyenne pour une conversation orale
  const durationSec = Math.round((totalWords / 140) * 60);
  const minutes = Math.floor(durationSec / 60);
  const seconds = durationSec % 60;
  const durationDisplay = minutes > 0 ? `${minutes} min ${seconds.toString().padStart(2, "0")}` : `${seconds}s`;

  // Vitesse de parole : 140 par construction (notre estimation). On affiche
  // uniquement si on a une diarization fiable (calcul réel par speaker)
  // → pour la V1 sans audio, on affiche la cible 140 comme indicatif.
  const wordsPerMinute = 140;

  // Ratio écoute/parole : parsing avec speakerMapping pour mapper correctement
  // le 1er locuteur en commercial (si l'utilisateur a coché "Moi parle en premier").
  // Cast nécessaire : le type historique a deux variantes coexistantes.
  const sm = session.speakerMapping as { commercialSpeaksFirst?: boolean } | null | undefined;
  const lines = parseTranscriptLines(text, sm);
  let salesWords = 0;
  let prospectWords = 0;
  for (const l of lines) {
    const w = l.content.split(/\s+/).filter(Boolean).length;
    if (l.speaker === "commercial") salesWords += w;
    else if (l.speaker === "prospect") prospectWords += w;
  }
  const totalTagged = salesWords + prospectWords;
  const salesRatioPct = totalTagged > 50 ? Math.round((salesWords / totalTagged) * 100) : null;

  return { totalWords, durationSec, durationDisplay, wordsPerMinute, salesRatioPct };
}

// Écran d'attente engageant pendant l'analyse Claude (~60s).
// 4 étapes simulées qui se débloquent selon le temps écoulé, timer en live.
// La vraie progression Claude n'est pas streamée en V1 (cf. archi temporaire) —
// le timing des étapes est calibré sur l'expérience moyenne observée.
function WaitingScreen({ createdAt, onBack }: { createdAt: string; onBack: () => void }) {
  const { t } = useTranslation();
  const startMs = new Date(createdAt).getTime();
  const [elapsedSec, setElapsedSec] = useState(() => Math.max(0, Math.floor((Date.now() - startMs) / 1000)));

  useEffect(() => {
    const id = setInterval(() => {
      setElapsedSec(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [startMs]);

  // 4 étapes : timing en secondes pour considérer chaque étape comme complétée
  const steps = [
    { label: t("salesCoach.detail.waitingStep1"), doneAt: 4 },
    { label: t("salesCoach.detail.waitingStep2"), doneAt: 6 },
    { label: t("salesCoach.detail.waitingStep3"), doneAt: 45 },
    { label: t("salesCoach.detail.waitingStep4"), doneAt: 55 },
  ];

  const minutes = Math.floor(elapsedSec / 60);
  const seconds = elapsedSec % 60;
  const elapsedDisplay = minutes > 0 ? `${minutes} min ${seconds.toString().padStart(2, "0")}` : `${seconds}s`;

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-2 text-sm mb-4 hover:underline" style={{ color: "#384872" }}>
        <ArrowLeft className="h-4 w-4" /> {t("salesCoach.detail.back")}
      </button>
      <Card className="p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ background: "#988EF2" }}>
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
              {t("salesCoach.detail.waitingTitle")}
            </h2>
            <div className="text-sm" style={{ color: "#4B5573" }}>
              {t("salesCoach.detail.waitingSubtitle", { elapsed: elapsedDisplay })}
            </div>
          </div>
        </div>

        {/* Étapes */}
        <div className="mt-6 space-y-3">
          {steps.map((step, idx) => {
            const isDone = elapsedSec >= step.doneAt;
            const isCurrent = !isDone && (idx === 0 || elapsedSec >= (steps[idx - 1]?.doneAt ?? 0));
            return (
              <div key={idx} className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  {isDone ? (
                    <CheckCircle2 className="h-5 w-5" style={{ color: "#1E7A3C" }} />
                  ) : isCurrent ? (
                    <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#5A4BBE" }} />
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2" style={{ borderColor: "#D1D5DB" }} />
                  )}
                </div>
                <div className={`text-sm leading-relaxed ${isDone ? "" : isCurrent ? "font-medium" : ""}`} style={{ color: isDone ? "#1E7A3C" : isCurrent ? "#384872" : "#9CA3AF" }}>
                  {step.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Barre de progression globale */}
        <div className="mt-6">
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "#E5E7EB" }}>
            <div
              className="h-full transition-all duration-1000"
              style={{
                width: `${Math.min(95, (elapsedSec / 60) * 100)}%`,
                background: "linear-gradient(90deg, #988EF2 0%, #5A4BBE 100%)",
              }}
            />
          </div>
        </div>

        {/* Tip pédagogique pour combler l'attente */}
        <div className="mt-6 p-4 rounded-lg" style={{ background: "#F8F7FF", borderLeft: "3px solid #988EF2" }}>
          <div className="text-xs uppercase tracking-wide font-semibold mb-1" style={{ color: "#5A4BBE" }}>
            {t("salesCoach.detail.waitingTipsTitle")}
          </div>
          <div className="text-sm" style={{ color: "#384872" }}>
            {pickTip(elapsedSec, t)}
          </div>
        </div>

        <div className="text-xs mt-6 text-center" style={{ color: "#9CA3AF" }}>
          {t("salesCoach.detail.waitingAutoRefresh")}
          {elapsedSec > 90 && ` · ${t("salesCoach.detail.waitingLongerHint")}`}
        </div>
      </Card>
    </div>
  );
}

// Mini bibliothèque de tips pour rendre l'attente plus utile (clés i18n).
const TIP_KEYS = [
  "salesCoach.detail.tip1",
  "salesCoach.detail.tip2",
  "salesCoach.detail.tip3",
  "salesCoach.detail.tip4",
  "salesCoach.detail.tip5",
  "salesCoach.detail.tip6",
];
function pickTip(elapsedSec: number, t: (key: string) => string): string {
  // Change de tip toutes les 12 secondes pour garder l'attention
  const idx = Math.floor(elapsedSec / 12) % TIP_KEYS.length;
  return t(TIP_KEYS[idx] ?? TIP_KEYS[0]!);
}

function DimensionRow({ dim }: { dim: DimensionScore }) {
  const { i18n } = useTranslation();
  const color = dim.score >= 80 ? "#7CBF94" : dim.score >= 60 ? "#A6D3F9" : dim.score >= 40 ? "#F6C847" : "#F49390";
  return (
    <div className="grid grid-cols-12 gap-4 items-start py-2">
      {/* Colonne gauche : label + barre + score */}
      <div className="col-span-12 md:col-span-5">
        <div className="flex justify-between items-baseline mb-1.5 gap-2">
          <span className="font-medium text-sm leading-tight" style={{ color: "#384872" }}>{pickScoringLabel(dim, i18n.language)}</span>
          <span className="font-bold text-sm whitespace-nowrap" style={{ color: "#384872" }}>{dim.score}<span className="text-xs font-normal" style={{ color: "#9CA3AF" }}>/10</span></span>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ background: "#E5E7EB" }}>
          <div className="h-full transition-all" style={{ width: `${dim.score * 10}%`, background: color }} />
        </div>
      </div>
      {/* Colonne droite : justification */}
      <div className="col-span-12 md:col-span-7">
        {dim.justification && (
          <div className="text-sm leading-relaxed" style={{ color: "#4B5573" }}>{dim.justification}</div>
        )}
      </div>
    </div>
  );
}

// Regroupement des dimensions par thème métier pour améliorer la lisibilité du débrief.
// Le mapping est fait sur les clés des critères transverses seedés
// (cf. scripts/seed-sales-coach-criteria.ts). Les clés inconnues tombent dans "Autres dimensions".
// Les libellés affichés passent par i18n via themeKey.
type DimensionTheme = { themeKey: string; emoji: string; order: number };
const DIMENSION_THEME_MAP: Record<string, DimensionTheme> = {
  // 🎭 Posture & savoir-être
  opening_posture: { themeKey: "salesCoach.detail.themePosture", emoji: "🎭", order: 1 },
  listen_ratio: { themeKey: "salesCoach.detail.themePosture", emoji: "🎭", order: 1 },
  active_listening: { themeKey: "salesCoach.detail.themePosture", emoji: "🎭", order: 1 },
  service_attitude: { themeKey: "salesCoach.detail.themePosture", emoji: "🎭", order: 1 },
  discourse_structure: { themeKey: "salesCoach.detail.themePosture", emoji: "🎭", order: 1 },
  // 🔍 Découverte & questionnement
  questioning_quality: { themeKey: "salesCoach.detail.themeDiscovery", emoji: "🔍", order: 2 },
  pain_identification: { themeKey: "salesCoach.detail.themeDiscovery", emoji: "🔍", order: 2 },
  business_impact: { themeKey: "salesCoach.detail.themeDiscovery", emoji: "🔍", order: 2 },
  // 🗺️ Décision & cartographie
  decision_mapping: { themeKey: "salesCoach.detail.themeDecisionMapping", emoji: "🗺️", order: 3 },
  methodology_coverage: { themeKey: "salesCoach.detail.themeDecisionMapping", emoji: "🗺️", order: 3 },
  // 💬 Argumentation & valeur
  value_pitch: { themeKey: "salesCoach.detail.themeArgumentation", emoji: "💬", order: 4 },
  // 🤝 Négociation & closing
  objection_handling: { themeKey: "salesCoach.detail.themeNegotiationClosing", emoji: "🤝", order: 5 },
  price_defense: { themeKey: "salesCoach.detail.themeNegotiationClosing", emoji: "🤝", order: 5 },
  next_step: { themeKey: "salesCoach.detail.themeNegotiationClosing", emoji: "🤝", order: 5 },
};
const FALLBACK_THEME: DimensionTheme = { themeKey: "salesCoach.detail.themeOther", emoji: "📋", order: 99 };

// Ordre d'affichage des thèmes (aligné sur MATRIX_THEMES côté backend).
// Les thèmes non listés (custom ou inconnus) tombent à la fin par défaut.
/**
 * `evaluation_criteria.theme` est un texte libre SANS variante anglaise en base : la fiche
 * anglaise affichait donc « POSTURE & SAVOIR-ÊTRE ». On traduit les thèmes du référentiel par
 * cette table, avec repli sur la valeur brute pour un thème saisi hors liste — mieux vaut un
 * intitulé français qu'un titre de groupe vide.
 */
const THEME_DB_I18N: Record<string, string> = {
  "🎭 Posture & savoir-être": "salesCoach.detail.matrixThemes.posture",
  "📞 Prospection téléphonique": "salesCoach.detail.matrixThemes.prospection",
  "🔍 Découverte & questionnement": "salesCoach.detail.matrixThemes.decouverte",
  "🗺️ Cadrage & qualification": "salesCoach.detail.matrixThemes.cadrage",
  "💬 Argumentation & valeur": "salesCoach.detail.matrixThemes.argumentation",
  "🤝 Négociation & closing": "salesCoach.detail.matrixThemes.negociation",
  "🤲 Relation & fidélisation": "salesCoach.detail.matrixThemes.relation",
  "Autres dimensions": "salesCoach.detail.matrixThemes.autres",
};

const THEME_DB_ORDER: Record<string, number> = {
  "🎭 Posture & savoir-être": 1,
  "📞 Prospection téléphonique": 2,
  "🔍 Découverte & questionnement": 3,
  "🗺️ Cadrage & qualification": 4,
  "💬 Argumentation & valeur": 5,
  "🤝 Négociation & closing": 6,
  "🤲 Relation & fidélisation": 7,
  "Autres dimensions": 99,
};

function groupDimensionsByTheme(dimensions: DimensionScore[]) {
  const groups = new Map<string, { emoji: string; label?: string; labelKey?: string; order: number; items: DimensionScore[]; isDbTheme: boolean }>();
  for (const d of dimensions) {
    // Priorité 1 (2026-06-02) : utiliser d.theme injecté depuis evaluation_criteria
    // par le backend (session.service.ts). Source de vérité = la matrice admin.
    // Priorité 2 (rétro-compat) : ancien mapping hardcodé DIMENSION_THEME_MAP
    // pour les débriefs créés avant le 2026-06-02 (pas de theme dans le JSON).
    if (d.theme) {
      // Sépare emoji + label pour matcher le style des thèmes legacy (emoji
      // affiché avec margin). Les chaînes DB ont la forme "🎭 Posture & savoir-être".
      const match = d.theme.match(/^(\p{Emoji}\p{Emoji_Modifier}*(?:‍\p{Emoji})*)\s+(.+)$/u);
      const emoji = match?.[1] ?? "";
      const label = match?.[2] ?? d.theme;
      const groupKey = d.theme;
      const order = THEME_DB_ORDER[d.theme] ?? 50;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, { emoji, label, labelKey: THEME_DB_I18N[d.theme], order, items: [], isDbTheme: true });
      }
      groups.get(groupKey)!.items.push(d);
    } else {
      const meta = DIMENSION_THEME_MAP[d.key] ?? FALLBACK_THEME;
      if (!groups.has(meta.themeKey)) {
        groups.set(meta.themeKey, { emoji: meta.emoji, order: meta.order, items: [], isDbTheme: false });
      }
      groups.get(meta.themeKey)!.items.push(d);
    }
  }
  // Tri par order croissant, puis dimensions internes par score descendant (les meilleures en haut)
  return Array.from(groups.entries())
    .map(([themeKey, data]) => ({ themeKey, ...data, items: [...data.items].sort((a, b) => b.score - a.score) }))
    .sort((a, b) => a.order - b.order);
}

function NextStepIndicator({ label, value }: { label: string; value: boolean }) {
  return (
    <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: value ? "#F0FDF4" : "#FFF5F5" }}>
      {value ? (
        <CheckCircle2 className="h-4 w-4" style={{ color: "#1E7A3C" }} />
      ) : (
        <XCircle className="h-4 w-4" style={{ color: "#C0423F" }} />
      )}
      <span className="text-xs font-medium" style={{ color: value ? "#1E7A3C" : "#C0423F" }}>{label}</span>
    </div>
  );
}

// Résumés courts pour les sections repliées (visibles dès que la section est fermée).
// t passé en paramètre car ces helpers sont appelés en dehors d'un composant React.
type TFn = (key: string, options?: Record<string, unknown>) => string;

function buildDimensionsSummary(dims: DimensionScore[], t: TFn, lang: string): string {
  if (!dims || dims.length === 0) return "";
  const avg = Math.round(dims.reduce((s, d) => s + d.score, 0) / dims.length);
  const weakest = [...dims].sort((a, b) => a.score - b.score)[0];
  // `weakest.label` est figé dans la langue du transcript : la fiche anglaise annonçait
  // « weak point: Engagement vers co-construction » sous un titre traduit.
  return t("salesCoach.detail.dimensionsSummary", { avg, weakest: weakest ? pickScoringLabel(weakest, lang) : "—" });
}
function buildNextStepSummary(ns: { announced: boolean; accepted: boolean; dated: boolean; mutualCommitment: boolean }, t: TFn): string {
  const checks = [ns.announced, ns.accepted, ns.dated, ns.mutualCommitment].filter(Boolean).length;
  return t("salesCoach.detail.nextStepSummary", { checked: checks });
}
function buildObjectionsSummary(objs: Array<{ handling: string }>, t: TFn): string {
  if (!objs || objs.length === 0) return "";
  const well = objs.filter((o) => o.handling === "well_handled").length;
  return t("salesCoach.detail.objectionsSummary", { count: objs.length, well, total: objs.length });
}

function formatDate(iso: string, lang?: string): string {
  try {
    const d = new Date(iso);
    const locale = lang === "en" ? "en-GB" : "fr-FR";
    return d.toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

// Header de section style BizCoach : cercle coloré avec icône blanche + titre Sora,
// cohérent avec le pattern utilisé sur DashboardPage (sections agents, coachs, challenges).
function SectionHeader({ icon, color, title, badge }: { icon: React.ReactNode; color: string; title: string; badge?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: color }}>
        {icon}
      </div>
      <h2 className="text-lg font-semibold flex-1" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
        {title}
      </h2>
      {badge}
    </div>
  );
}

// Section repliable (accordéon) — utilise SectionHeader + ChevronDown/Up,
// cohérent avec le style BizCoach (rounded-xl border, fond blanc, transitions douces).
function CollapsibleSection({
  title,
  icon,
  iconColor,
  defaultOpen = false,
  badge,
  summary,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  iconColor: string;
  defaultOpen?: boolean;
  badge?: string;
  summary?: string; // 1 ligne résumée visible quand section repliée
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border bg-white overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full p-4 md:p-5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left min-h-[64px]"
        aria-expanded={open}
      >
        <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: iconColor }}>
          <span className="text-white">{icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base md:text-lg font-semibold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
            {title}
          </h2>
          {summary && !open && (
            <p className="text-xs md:text-sm text-gray-500 mt-0.5 truncate">{summary}</p>
          )}
        </div>
        {badge && (
          <Badge variant="outline" className="shrink-0" style={{ color: iconColor, borderColor: iconColor }}>{badge}</Badge>
        )}
        {open ? (
          <ChevronUp className="h-5 w-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400 shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t pt-4">
          {children}
        </div>
      )}
    </div>
  );
}
